import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TrackCard from "../components/TrackCard";
import ArtistCard from "../components/ArtistCard";
import { Heart, History, ListMusic, UserPlus, Library as LibIcon, Plus } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "playlists", label: "Playlists", icon: ListMusic },
  { key: "liked", label: "Likes", icon: Heart },
  { key: "history", label: "Historique", icon: History },
  { key: "following", label: "Abonnements", icon: UserPlus },
];

export const LibraryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("playlists");
  const [data, setData] = useState({ playlists: [], liked: [], history: [], following: [], castings: [], applications: [] });
  const [newTitle, setNewTitle] = useState("");

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    Promise.all([
      api.get("/playlists", { params: { user_id: user.user_id, public_only: false } }).then((r) => ["playlists", r.data]).catch(() => ["playlists", []]),
      api.get("/me/liked").then((r) => ["liked", r.data]).catch(() => ["liked", []]),
      api.get("/me/history").then((r) => ["history", r.data]).catch(() => ["history", []]),
      api.get("/me/following").then((r) => ["following", r.data]).catch(() => ["following", []]),
      api.get("/me/castings").then((r) => ["castings", r.data]).catch(() => ["castings", []]),
      api.get("/me/applications").then((r) => ["applications", r.data]).catch(() => ["applications", []]),
    ]).then((results) => {
      const d = {};
      results.forEach(([k, v]) => { d[k] = v; });
      setData(d);
    });
  }, [user, navigate]);

  if (!user) return null;

  const createPlaylist = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      const { data: pl } = await api.post("/playlists", { title: newTitle, is_public: true });
      setData((d) => ({ ...d, playlists: [pl, ...d.playlists] }));
      setNewTitle("");
      toast.success("Playlist créée");
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="px-4 md:px-12 py-10 max-w-7xl mx-auto pb-32" data-testid="library-page">
      <p className="text-[10px] tracking-[0.25em] uppercase text-brand-orange font-bold mb-2 inline-flex items-center gap-2">
        <LibIcon size={12} /> Ma bibliothèque
      </p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-10">Votre univers musical</h1>

      <div className="flex gap-1 mb-8 overflow-x-auto border-b border-white/10" data-testid="library-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-sm font-semibold inline-flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? "border-brand-red text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon size={14} /> {t.label}
            <span className="text-xs text-white/40">({data[t.key]?.length || 0})</span>
          </button>
        ))}
      </div>

      {tab === "playlists" && (
        <div data-testid="playlists-tab">
          <form onSubmit={createPlaylist} className="flex gap-2 mb-6 max-w-lg">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Nom de votre nouvelle playlist..."
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-2.5 outline-none focus:border-brand-red/50 text-sm"
              data-testid="library-new-playlist-input"
            />
            <button className="btn-primary text-sm inline-flex items-center gap-1" data-testid="library-create-playlist">
              <Plus size={14} /> Créer
            </button>
          </form>
          {data.playlists.length === 0 ? (
            <EmptyState label="Pas encore de playlist. Créez-en une pour organiser vos morceaux préférés !" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.playlists.map((pl) => (
                <Link key={pl.playlist_id} to={`/playlist/${pl.playlist_id}`} className="group" data-testid={`playlist-card-${pl.playlist_id}`}>
                  <div className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-brand-red via-brand-orange to-brand-yellow flex items-center justify-center mb-3 group-hover:scale-[1.02] transition-transform">
                    <ListMusic size={48} className="text-white/80" />
                  </div>
                  <p className="font-semibold truncate">{pl.title}</p>
                  <p className="text-xs text-white/50">{pl.tracks_count ?? pl.track_ids?.length ?? 0} morceaux · {pl.is_public ? "Public" : "Privée"}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "liked" && (
        data.liked.length === 0 ? <EmptyState label="Aucun morceau aimé pour l'instant." />
        : <div className="grid grid-cols-2 md:grid-cols-4 gap-6">{data.liked.map((t) => <TrackCard key={t.track_id} track={t} list={data.liked} />)}</div>
      )}

      {tab === "history" && (
        data.history.length === 0 ? <EmptyState label="Votre historique d'écoute s'affichera ici." />
        : <div className="grid grid-cols-2 md:grid-cols-4 gap-6">{data.history.map((t) => <TrackCard key={t.track_id} track={t} list={data.history} />)}</div>
      )}

      {tab === "following" && (
        data.following.length === 0 ? <EmptyState label="Vous ne suivez personne pour l'instant. Découvrez des artistes !" />
        : <div className="grid grid-cols-3 md:grid-cols-5 gap-2">{data.following.map((a) => <ArtistCard key={a.user_id} artist={a} />)}</div>
      )}

      {tab === "castings" && (
        data.castings.length === 0 ? <EmptyState label="Aucune annonce publiée. Créez-en une depuis la page Appel à candidatures !" />
        : <div className="space-y-3" data-testid="my-castings">
            {data.castings.map((c) => (
              <Link key={c.casting_id} to={`/casting/${c.casting_id}`} className="flex items-center gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05]" data-testid={`my-casting-${c.casting_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{c.title}</p>
                  <p className="text-xs text-white/50">{c.city || "—"}, {c.country} · {c.applications_count} candidatures · {c.status}</p>
                </div>
                <span className={`tag-pill ${c.status === "open" ? "bg-green-500/15 text-green-300 border-green-500/30" : ""}`}>{c.status}</span>
              </Link>
            ))}
          </div>
      )}

      {tab === "applications" && (
        data.applications.length === 0 ? <EmptyState label="Aucune candidature envoyée. Explorez les opportunités !" />
        : <div className="space-y-3" data-testid="my-applications">
            {data.applications.map((a) => (
              <Link key={a.application_id} to={`/casting/${a.casting_id}`} className="flex items-center gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05]" data-testid={`my-app-${a.application_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{a.casting?.title || "Annonce supprimée"}</p>
                  <p className="text-xs text-white/50">Par {a.casting?.recruiter_name} · {new Date(a.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
                <span className={`tag-pill ${
                  a.status === "accepted" ? "bg-green-500/15 text-green-300 border-green-500/30" :
                  a.status === "rejected" ? "bg-red-500/15 text-red-300 border-red-500/30" :
                  "bg-brand-yellow/15 text-brand-yellow border-brand-yellow/30"
                }`}>
                  {a.status === "accepted" ? "Retenu" : a.status === "rejected" ? "Décliné" : "En attente"}
                </span>
              </Link>
            ))}
          </div>
      )}
    </div>
  );
};

const EmptyState = ({ label }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
    <p className="text-white/60">{label}</p>
  </div>
);

export default LibraryPage;
